import React, { useEffect, useState } from "react";
import { printElement } from "../../master/Print";
import { getStudents } from "./Master";
import { formatToDate } from "../../master/FormatToDateTime";
import StudentTable from "./StudentTable";

const readable = (value) =>
  value === null || value === undefined || value === "" ? "—" : value;

const formatDate = (value) => {
  if (!value) return "—";

  try {
    return formatToDate(value) || "—";
  } catch {
    return String(value);
  }
};

export default function DashboardModal({
  setLoading,
  allFilter,
  setAllFilter,
}) {
  const [printStudents, setPrintStudents] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [shouldPrint, setShouldPrint] = useState(false);

  // Fetch one page using the existing API function.
  const fetchStudentPage = async (filters) => {
    let result = null;

    await getStudents({
      setLoading: () => {},
      filters,
      setData: (value) => {
        result = value;
      },
    });

    if (!result || !Array.isArray(result.data)) {
      throw new Error(
        "The student API did not return the expected paginated data.",
      );
    }

    return result;
  };

  // Fetch all pages with the same filters before printing.
  const handlePrint = async () => {
    if (isPrinting) return;

    setIsPrinting(true);

    try {
      const baseFilters = {
        ...allFilter,
        page: 1,
      };

      const firstPage = await fetchStudentPage(baseFilters);
      const allStudents = [...firstPage.data];

      const total = Number(firstPage.total || allStudents.length);
      const pageSize = Number(firstPage.per_page || allStudents.length || 1);

      // Laravel pagination normally provides last_page.
      const lastPage = Math.max(
        1,
        Number(firstPage.last_page || Math.ceil(total / pageSize)),
      );

      for (let page = 2; page <= lastPage; page++) {
        const pageData = await fetchStudentPage({
          ...baseFilters,
          page,
        });

        if (pageData.data.length === 0) break;

        allStudents.push(...pageData.data);
      }

      if (allStudents.length === 0) {
        window.alert("No students found to print.");
        return;
      }

      setPrintStudents(allStudents);
      setShouldPrint(true);
    } catch (error) {
      console.error("Unable to fetch all students for printing:", error);

      window.alert(
        error?.message || "Unable to fetch all students. Please try again.",
      );
    } finally {
      setIsPrinting(false);
    }
  };

  // Wait until React renders the complete print table.
  useEffect(() => {
    if (!shouldPrint || printStudents.length === 0) return;

    const timer = setTimeout(() => {
      printElement(
        "AllStudentsPrint",
        `
         @page {
  size: landscape;
  margin: 8mm;
}

html,
body {
  margin: 0 !important;
  padding: 0 !important;
  height: auto !important;
}

#AllStudentsPrint {
  display: block !important;
  position: static !important;
  width: 100% !important;
  height: auto !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: visible !important;
}

#AllStudentsPrint h2 {
  margin: 0 0 4px !important;
  font-size: 16px !important;
}

#AllStudentsPrint p {
  margin: 0 0 6px !important;
  font-size: 10px !important;
}

#AllStudentsPrint table {
  display: table !important;
  width: 100% !important;
  margin: 0 !important;
  border-collapse: collapse !important;
  font-size: 9px !important;
  page-break-before: auto !important;
  page-break-after: auto !important;
  page-break-inside: auto !important;
  break-before: auto !important;
  break-after: auto !important;
  break-inside: auto !important;
}

#AllStudentsPrint thead {
  display: table-header-group !important;
}

#AllStudentsPrint tbody {
  display: table-row-group !important;
  page-break-inside: auto !important;
  break-inside: auto !important;
}

#AllStudentsPrint tr {
  page-break-inside: avoid !important;
  break-inside: avoid !important;
}

#AllStudentsPrint th,
#AllStudentsPrint td {
  padding: 4px !important;
  border: 1px solid #999 !important;
  color: #000 !important;
  text-align: left !important;
  white-space: normal !important;
  overflow-wrap: anywhere !important;
}
        `,
      );

      setShouldPrint(false);
    }, 100);

    return () => clearTimeout(timer);
  }, [shouldPrint, printStudents]);

  return (
    <>
      <div
        className="modal fade"
        id="MainModal"
        tabIndex={-1}
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header bg-label-primary py-3">
              <h1 className="modal-title fs-5" id="exampleModalLabel">
                Student List
              </h1>

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>

            <div className="modal-body">
              {allFilter?.table === "student" && (
                <>
                  <div className="row g-2 align-items-center mb-3">
                    <div className="col-sm-8">
                      <h4 className="text-primary m-0">Student List</h4>

                      <p className="m-0">
                        {allFilter?.type === "active"
                          ? "Students currently active and enrolled in ongoing batches."
                          : allFilter?.type === "inactive"
                            ? "Students enrolled in active batches whose status is marked as inactive."
                            : "Student records matching the selected dashboard filters."}
                      </p>
                    </div>

                    <div className="col-sm-4 text-end">
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={handlePrint}
                        disabled={isPrinting}
                        title="Print all students"
                      >
                        <i
                          className={
                            isPrinting
                              ? "ri-loader-4-line me-1"
                              : "ri-printer-line me-1"
                          }
                        />
                        {isPrinting ? "Preparing all students..." : "Print All"}
                      </button>
                    </div>
                  </div>

                  <hr />

                  {/* Normal paginated table */}
                  <StudentTableWrapper
                    allFilter={allFilter}
                    setAllFilter={setAllFilter}
                  />
                </>
              )}
            </div>

            <div className="modal-footer bg-label-primary py-3">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Separate print-only table containing every fetched page */}
      <div id="AllStudentsPrint" style={{ display: "none" }}>
        <h2>Student List</h2>

        <p>Total students: {printStudents.length}</p>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Student / Reg. No.</th>
              <th>Father Name</th>
              <th>Mother Name</th>
              <th>Mobile</th>
              <th>DOB</th>
              <th>Batch</th>
              <th>Course</th>
              <th>Admission Date</th>
            </tr>
          </thead>

          <tbody>
            {printStudents.map((student, index) => (
              <tr key={student.id ?? index}>
                <td>{index + 1}</td>
                <td>
                  {readable(student.name)}
                  <br />
                  Reg. No.: {readable(student.registration_no)}
                </td>
                <td>{readable(student.father_name)}</td>
                <td>{readable(student.mother_name)}</td>
                <td>{readable(student.phone)}</td>
                <td>{formatDate(student.dob)}</td>
                <td>{readable(student?.course_and_fee_info?.batch?.title)}</td>
                <td>{readable(student?.course_and_fee_info?.course?.title)}</td>
                <td>{formatDate(student.admission_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// Keep using your existing StudentTable component.

function StudentTableWrapper({ allFilter, setAllFilter }) {
  return (
    <div id="PrintDiv">
      <StudentTable filters={allFilter} setFilters={setAllFilter} />
    </div>
  );
}
